import { useFlaggingCallback } from '@databases/flags';
import ValidationStatus, {
  ValidationStatusProps,
} from '@databases/form/ValidationStatus';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import {
  DatabaseField,
  DatabaseValue,
  RawDatabaseValue,
} from '@databases/types/database';
import {
  DatabaseChange,
  DatabaseChangeType,
  ValueChange,
} from '@databases/utils/database/changesets';
import { convertType, getValidationError } from '@databases/utils/database/field-types';
import {
  getConvertedDatabaseValue,
  getDatabaseEntryRequired,
  getDatabaseFieldRequired,
} from '@databases/utils/database/utils';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import composeRefs from '@seznam/compose-react-refs';
import React, {
  forwardRef,
  FunctionComponent,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useIntl } from 'react-intl-next';

import { useScope } from '../Scope';
import { FIELD_TYPES } from '../field-types/fieldTypes';

import { useLoadDependencies } from './utils';

export interface DatabaseValueEditProps
  extends Omit<
    InternalDatabaseValueEditProps,
    'field' | 'value' | 'onChange' | 'addDraftedDatabaseUpdate'
  > {
  contentId: string;
  fieldId: string;
  entryId: string;
  noValidation?: boolean;
  handleChange?: (value: DatabaseValue, change: ValueChange | null) => void;
}

/**
 * Renders a database value. The right view component is automatically chosen based on the field type of the value.
 */
const DatabaseValueEdit: FunctionComponent<DatabaseValueEditProps> = ({
  contentId,
  fieldId,
  entryId,
  noValidation,
  handleChange,
  ...props
}) => {
  const scope = useScope();
  const [field, value] = defaultStoreUtils.useEqualSelector((state) => {
    const database = selectDatabaseByContentIdRequired(state, {
      contentId,
    });
    const entry = getDatabaseEntryRequired(database, entryId);
    const field = getDatabaseFieldRequired(database, fieldId);
    const value = getConvertedDatabaseValue(database, field, entry, state);
    return [field, value] as const;
  });

  const dispatch = defaultStoreUtils.useDispatch();

  const defaultHandleChange = useFlaggingCallback(
    (value: DatabaseValue, change: ValueChange | null) => {
      dispatch(
        addChangeToDraft(contentId, scope, {
          type: DatabaseChangeType.SET_VALUE,
          fieldId: fieldId,
          entryId: entryId,
          value,
          change,
        })
      );
    },
    [dispatch, contentId, scope, fieldId, entryId]
  );

  const handleAddChangeToDraft = useFlaggingCallback(
    (change: DatabaseChange) => {
      dispatch(addChangeToDraft(contentId, scope, change));
    },
    [contentId, dispatch, scope]
  );

  return (
    <InternalDatabaseValueEdit
      {...props}
      contentId={contentId}
      entryId={entryId}
      field={field}
      value={value}
      onChange={handleChange || defaultHandleChange}
      addChangeToDraft={handleAddChangeToDraft}
      noValidation={noValidation}
    />
  );
};

export default DatabaseValueEdit;

export interface InternalDatabaseValueEditProps {
  contentId: string;
  entryId?: string;
  field: DatabaseField;
  value: DatabaseValue;
  onChange: (value: DatabaseValue, change: ValueChange | null) => void;
  onFocus?: (e?: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e?: React.FocusEvent<HTMLElement>) => void;
  autoFocus?: boolean;
  isSeamless?: boolean;
  addChangeToDraft?: (change: DatabaseChange) => void;
  noValidation?: boolean;
  isDefaultValue?: boolean;
  placeholder?: string;
  isBulkEdit?: boolean;
  appearance?: 'default' | 'none' | 'subtle' | undefined;
}

/**
 * Renders a form field to edit a database value. Automatically picks the right edit component based on the
 * field type of the value.
 */
export const InternalDatabaseValueEdit = forwardRef<
  HTMLInputElement,
  InternalDatabaseValueEditProps
>(function InternalDatabaseValueEdit(
  { contentId, entryId, field, value, onChange, noValidation, ...props },
  inputRef
) {
  const Component = FIELD_TYPES[field.type].edit as any;
  const { formatMessage } = useIntl();
  if (!Component) {
    throw new Error(
      formatMessage(messages['error-message.unknown-field-type'], {
        fieldType: field.type,
      })
    );
  }

  const handleChange = useFlaggingCallback(
    (val: RawDatabaseValue, change: ValueChange | null) => {
      onChange({ type: field.type, value: val } as DatabaseValue, change);
    },
    [field.type, onChange]
  );

  const store = defaultStoreUtils.useStore();

  // Not using useSelector() here, as some conversions generate random IDs, meaning that the converted value changes each time.
  // This way we only run the conversion when the original value changes.
  const convertedValue = useMemo(() => {
    return convertType({ field, value }, field.type, store.getState());
  }, [field, store, value]);

  useLoadDependencies({ contentId, entryId, field, convertedValue });

  const scope = useScope();
  const validationError = defaultStoreUtils.useSelector((state) => {
    if (noValidation) {
      return undefined;
    }
    const databaseDraft = selectDatabaseDraftByContentId(state, {
      contentId,
      scope,
      includeDeleted: true,
    });
    const entry =
      entryId != null ? getDatabaseEntryRequired(databaseDraft, entryId) : undefined;
    return getValidationError({ database: databaseDraft, field, entry, value }, state);
  });

  const ref = useRef();
  const renderComponent: ValidationStatusProps['children'] = useCallback(
    (validationProps) => (
      <Component
        {...validationProps}
        field={field}
        value={convertedValue.value}
        onChange={handleChange}
        contentId={contentId}
        entryId={entryId}
        ref={composeRefs(ref, inputRef)}
        {...props}
      />
    ),
    [
      Component,
      contentId,
      convertedValue.value,
      entryId,
      field,
      handleChange,
      inputRef,
      props,
    ]
  );

  return (
    <ValidationStatus
      error={
        validationError
          ? formatMessage(messages[validationError as keyof typeof messages])
          : undefined
      }
    >
      {renderComponent}
    </ValidationStatus>
  );
});
